import React from 'react';
import { DollarSign, Eye, Download, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { months, statusColors, statusLabels } from './utils/payrollConstants';
import { formatCurrency, calculateTotalDeductions } from './utils/payrollUtils';

interface PayrollTableProps {
  payrolls: any[];
  loading: boolean;
  selectedMonth: number;
  selectedYear: number;
  employees: any[];
  positions: any[];
  onDetailClick: (payroll: any) => void;
  onSlipClick: (payroll: any) => void;
  onApproveClick: (id: number) => void;
  onPaymentClick: (id: number) => void;
  getEmployeeName: (empId: number) => string;
  getEmployeeId: (empId: number) => string;
  getPositionName: (posId: number) => string;
}

export default function PayrollTable({
  payrolls,
  loading,
  selectedMonth,
  selectedYear,
  employees,
  positions,
  onDetailClick,
  onSlipClick,
  onApproveClick,
  onPaymentClick,
  getEmployeeName,
  getEmployeeId,
  getPositionName,
}: PayrollTableProps) {
  const monthLabel = months.find(m => m.value === selectedMonth)?.label || '';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Slip Gaji - {monthLabel} {selectedYear}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Pegawai</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Jabatan</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">
                  Gaji Pokok
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Lembur</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">
                  Bonus KPI
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Potongan</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">
                  Total Gaji
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <tr key={i} className="border-b">
                      {Array(9)
                        .fill(0)
                        .map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                          </td>
                        ))}
                    </tr>
                  ))
              ) : payrolls.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">Belum ada data payroll</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Klik "Generate Payroll" untuk membuat
                    </p>
                  </td>
                </tr>
              ) : (
                payrolls.map(payroll => {
                  const emp = employees.find(e => e.id === payroll.employee_id);
                  const totalDeductions = calculateTotalDeductions(payroll);

                  return (
                    <tr key={payroll.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <span className="font-semibold text-emerald-700 text-sm">
                              {getEmployeeName(payroll.employee_id)?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {getEmployeeName(payroll.employee_id)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {getEmployeeId(payroll.employee_id)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{getPositionName(emp?.position_id)}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {formatCurrency(payroll.base_salary)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-orange-600">
                        {formatCurrency(payroll.overtime_pay)}
                        {payroll.overtime_hours > 0 && (
                          <p className="text-xs text-gray-500">{payroll.overtime_hours}h</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-purple-600">
                        {formatCurrency(payroll.kpi_bonus)}
                        {payroll.kpi_score > 0 && (
                          <p className="text-xs text-gray-500">Score: {payroll.kpi_score}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-red-600">
                        -{formatCurrency(totalDeductions)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-sm">
                        {formatCurrency(payroll.net_salary)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          className={`${statusColors[payroll.status] || 'bg-gray-100'} font-bold px-3 py-1`}
                        >
                          {statusLabels[payroll.status] || payroll.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDetailClick(payroll)}
                            title="Detail"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onSlipClick(payroll)}
                            title="Slip Gaji"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {payroll.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onApproveClick(payroll.id)}
                              title="Approve"
                            >
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                          {payroll.status === 'processed' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onPaymentClick(payroll.id)}
                              title="Bayar"
                            >
                              <DollarSign className="w-4 h-4 text-blue-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
